/**
 * BlockConfigPanel
 *
 * Fixed right-side panel for configuring a selected block's props.
 * Renders BlockSchemaForm driven by the block's registry schema.
 */
import React from 'react';
import { X, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { getBlockEntry } from './componentRegistry';
import BlockSchemaForm from './BlockSchemaForm';
import type { BlockInstance } from './types';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */
export interface BlockConfigPanelProps {
  block: BlockInstance | null;
  onUpdate: (blockId: string, props: Record<string, unknown>) => void;
  onDelete: (blockId: string) => void;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function BlockConfigPanel({
  block,
  onUpdate,
  onDelete,
  onClose,
}: BlockConfigPanelProps) {
  const entry = block ? getBlockEntry(block.blockKey) : null;

  if (!block) {
    return (
      <div
        className="w-80 shrink-0 bg-white border-l border-border flex items-center justify-center"
        style={{ minHeight: '100%' }}
      >
        <div className="text-center px-6">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <span className="text-muted-foreground text-lg">⊞</span>
          </div>
          <p className="text-sm font-medium text-foreground">Select a block</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click any block on the canvas to configure its properties.
          </p>
        </div>
      </div>
    );
  }

  const schema = entry?.schema ?? [];
  const label = entry?.label ?? block.blockKey;
  const category = entry?.category ?? 'custom';

  const handleFieldChange = (key: string, value: unknown) => {
    onUpdate(block.id, { ...block.props, [key]: value });
  };

  const handleLayoutHint = (hint: string) => {
    // layoutHint is a top-level field on BlockInstance, not in props.
    // We pass it via onUpdate with a special __layoutHint key that the
    // parent can intercept.
    onUpdate(block.id, { ...block.props, __layoutHint: hint === 'none' ? undefined : hint });
  };

  const currentLayoutHint = block.layoutHint ?? 'main';

  return (
    <div className="w-80 shrink-0 bg-white border-l border-border flex flex-col shadow-[-4px_0_16px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{label}</p>
          <Badge variant="outline" className="text-[10px] py-0 mt-0.5">
            {category}
          </Badge>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground"
        >
          <X size={14} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Layout Hint — only shown in data-room mode contexts */}
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">Layout Slot</Label>
          <Select value={currentLayoutHint} onValueChange={handleLayoutHint}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="main">Main Column</SelectItem>
              <SelectItem value="sidebar">Sidebar</SelectItem>
              <SelectItem value="full">Full Width</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground/70">
            Used in Data-Room layout. Ignored for single-column pages.
          </p>
        </div>

        <div className="border-t border-border/50" />

        {/* Field editors */}
        <BlockSchemaForm
          schema={schema}
          values={block.props}
          onChange={handleFieldChange}
        />
      </div>

      {/* Footer — delete */}
      <div className="px-4 py-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          onClick={() => onDelete(block.id)}
        >
          <Trash2 size={13} className="mr-1.5" />
          Delete Block
        </Button>
      </div>
    </div>
  );
}
